import { ActivityAdvancedFilters } from "./ActivityAdvancedFilters";
import { activitySearchFilter, emptyActivitySearch } from "./activityFilters";
import { createPortal } from "react-dom";
import { useMemo, useState } from "react";

import type { Dispatch, SetStateAction } from "react";

import { InfiniteListBase, type Identifier, useGetIdentity } from "ra-core";

import { SlidersHorizontal } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { ActivitySearch } from "./activityFilters";

import { ActivityLogContext } from "./ActivityLogContext";

import { ActivityLogIterator } from "./ActivityLogIterator";

type ActivityScope = "mine" | "team" | "all";

type ActivityLogProps = {
  controlsContainer?: HTMLElement | null;
  companyId?: Identifier;

  pageSize?: number;

  context?: "company" | "contact" | "deal" | "all";

  showTypeFilters?: boolean;

  filtersInHeader?: boolean;

  /*
   * На Dashboard приходит
   * общий переключатель страницы.
   *
   * Если scope не передан,
   * ActivityLog может работать
   * самостоятельно.
   */
  scope?: ActivityScope;
};

type ActivityFilter =
  | "created"
  | "updated"
  | "status"
  | "comments"
  | "deleted"
  | "expenses"
  | "other";

type FilterState = Record<ActivityFilter, boolean>;

const defaultFilters: FilterState = {
  created: true,
  updated: true,
  status: true,
  comments: true,
  deleted: true,
  expenses: true,
  other: true,
};

const filterLabels: Array<{
  key: ActivityFilter;

  label: string;
}> = [
  {
    key: "created",
    label: "Создание",
  },

  {
    key: "updated",
    label: "Редактирование",
  },

  {
    key: "status",
    label: "Статус",
  },

  {
    key: "comments",
    label: "Комментарии",
  },

  {
    key: "deleted",
    label: "Удаление",
  },

  {
    key: "expenses",
    label: "Расходы",
  },

  {
    key: "other",
    label: "Прочая активность",
  },
];

const ActivityControls = ({
  enabled,
  setEnabled,
  compact = false,
  inHeader = false,
  search,
  setSearch,
  reset,
}: {
  search: ActivitySearch;
  setSearch: (search: ActivitySearch) => void;
  reset: () => void;
  enabled: FilterState;

  setEnabled: Dispatch<SetStateAction<FilterState>>;

  compact?: boolean;
  inHeader?: boolean;
}) => {
  const enabledCount = Object.values(enabled).filter(Boolean).length;

  const selectAll = () => {
    setEnabled({
      created: true,
      updated: true,
      status: true,
      comments: true,
      deleted: true,
      expenses: true,
      other: true,
    });
  };

  const selectNone = () => {
    setEnabled({
      created: false,
      updated: false,
      status: false,
      comments: false,
      deleted: false,
      expenses: false,
      other: false,
    });
  };

  return (
    <div
      className={
        compact
          ? "flex items-center"
          : inHeader
            ? "absolute right-0 -top-9 z-10 flex items-center gap-2"
            : "mb-4 flex flex-wrap justify-end gap-2"
      }
    >
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={compact ? "h-8 px-2 text-xs gap-1" : undefined}
          >
            <SlidersHorizontal
              className={compact ? "size-3.5" : "mr-2 h-4 w-4"}
            />
            Фильтр
            <span
              className={
                compact
                  ? "text-[10px] text-muted-foreground"
                  : "ml-2 text-xs text-muted-foreground"
              }
            >
              {enabledCount}/{filterLabels.length}
            </span>
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="end"
          collisionPadding={16}
          style={{
            maxHeight:
              "min(70dvh, var(--radix-popover-content-available-height))",
          }}
          className="w-80 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-2xl p-3"
        >
          <ActivityAdvancedFilters
            value={search}
            onChange={setSearch}
            onReset={reset}
          />
          <p className="mb-2 text-sm font-medium">Показывать события</p>

          <hr className="my-2" />

          {filterLabels.map((filter) => (
            <label
              key={filter.key}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm hover:bg-muted"
            >
              <Checkbox
                className="cursor-pointer"
                checked={enabled[filter.key]}
                onCheckedChange={(checked) =>
                  setEnabled((current) => ({
                    ...current,
                    [filter.key]: checked === true,
                  }))
                }
              />
              {filter.label}
            </label>
          ))}

          <hr className="my-2" />

          <button
            type="button"
            onClick={selectAll}
            className="w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
          >
            Выбрать всё
          </button>

          <button
            type="button"
            onClick={selectNone}
            className="w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
          >
            Снять всё
          </button>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export function ActivityLog({
  controlsContainer,
  companyId,
  pageSize = 20,
  context = "all",
  showTypeFilters = false,
  filtersInHeader = false,
  scope: controlledScope,
}: ActivityLogProps) {
  const { identity } = useGetIdentity();

  const [search, setSearch] = useState(emptyActivitySearch);
  const [enabled, setEnabled] = useState<FilterState>(defaultFilters);

  const scope: ActivityScope = identity?.administrator
    ? (controlledScope ?? "all")
    : "mine";

  /*
   * Получаем активные категории
   * фильтра.
   */
  const enabledCategories = useMemo(
    () =>
      filterLabels
        .filter((filter) => enabled[filter.key])
        .map((filter) => filter.key),
    [enabled],
  );

  /*
   * ВАЖНО:
   *
   * Этот filter отправляется
   * непосредственно в PostgREST.
   *
   * Значит Supabase сначала
   * отбирает правильные записи,
   * а УЖЕ ПОТОМ делает
   * pagination / "Показать ещё".
   */
  const serverFilter = useMemo(() => {
    const filter: Record<string, unknown> = { ...activitySearchFilter(search) };

    if (companyId != null) {
      filter["company_id@eq"] = companyId;
    }

    /*
     * МОИ ЗАДАЧИ
     *
     * Контакты, компании и события задач,
     * где я являюсь
     * исполнителем или автором поручения.
     *
     * Сюда входят:
     *
     * Я -> Я
     * Менеджер -> Я
     * Другой сотрудник -> Я
     */
    if (scope === "mine") {
      filter["personal_visible@eq"] = true;
    }

    /*
     * КОМАНДА
     *
     * Обычная CRM-активность
     * + только рабочие задачи,
     * где автор и исполнитель
     * разные.
     *
     * Личные:
     * Пётр -> Пётр
     * сюда не попадают.
     */
    if (scope === "team") {
      filter["team_visible@eq"] = true;
    }

    /*
     * Фильтр типов событий
     * тоже выполняется
     * ДО пагинации.
     */
    if (enabledCategories.length === 0) {
      /*
       * Заведомо несуществующая
       * категория.
       *
       * Если сняты все галочки,
       * база корректно вернёт
       * пустой список.
       */
      filter["activity_category@eq"] = "__none__";
    } else if (enabledCategories.length < filterLabels.length) {
      filter["activity_category@in"] = `(${enabledCategories.join(",")})`;
    }

    return filter;
  }, [companyId, scope, enabledCategories, search]);

  return (
    <ActivityLogContext.Provider value={context}>
      <InfiniteListBase
        resource="activity_log"
        /*
         * Больше никакой
         * клиентской фильтрации.
         *
         * Всё делает Supabase.
         */
        filter={serverFilter}
        sort={{
          field: "date",

          order: "DESC",
        }}
        perPage={pageSize}
        disableSyncWithLocation
      >
        {showTypeFilters &&
          (controlsContainer ? (
            createPortal(
              <ActivityControls
                enabled={enabled}
                search={search}
                setSearch={setSearch}
                reset={() => {
                  setSearch(emptyActivitySearch);
                  setEnabled(defaultFilters);
                }}
                setEnabled={setEnabled}
                compact
              />,
              controlsContainer,
            )
          ) : (
            <ActivityControls
              enabled={enabled}
              search={search}
              setSearch={setSearch}
              reset={() => {
                setSearch(emptyActivitySearch);
                setEnabled(defaultFilters);
              }}
              setEnabled={setEnabled}
              inHeader={filtersInHeader}
            />
          ))}

        <ActivityLogIterator />
      </InfiniteListBase>
    </ActivityLogContext.Provider>
  );
}
